/**
 * Theme Loading Indicator
 *
 * PI7: Shows a subtle progress bar at the top of the screen during theme transitions.
 *
 * Features:
 * - Fixed position at top of viewport
 * - Smooth animation from 0% to 100% over 300ms
 * - Respects reduced motion preferences
 * - Works with all themes (uses primary color)
 * - Auto-hides when transition completes
 */

import { useEffect, useState } from 'react'
import { onTransitionChange, getTransitionDuration } from '@/lib/theme-transition'

export function ThemeLoadingIndicator() {
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [progress, setProgress] = useState(0)

  useEffect(() => {
    // Subscribe to theme transition state changes
    const unsubscribe = onTransitionChange((transitioning) => {
      setIsTransitioning(transitioning)

      if (transitioning) {
        // Reset progress when transition starts
        setProgress(0)

        // Animate progress from 0 to 100 over transition duration
        const duration = getTransitionDuration()

        if (duration === 0) {
          // No transition (reduced motion) - immediately complete
          setProgress(100)
        } else {
          // Smooth progress animation
          const startTime = Date.now()
          const animateProgress = () => {
            const elapsed = Date.now() - startTime
            const newProgress = Math.min((elapsed / duration) * 100, 100)
            setProgress(newProgress)

            if (newProgress < 100) {
              requestAnimationFrame(animateProgress)
            }
          }
          requestAnimationFrame(animateProgress)
        }
      } else {
        // Transition complete - ensure progress is 100%
        setProgress(100)

        // Small delay before hiding to show completion
        setTimeout(() => {
          setProgress(0)
        }, 100)
      }
    })

    return unsubscribe
  }, [])

  // Don't render anything if not transitioning
  if (!isTransitioning && progress === 0) {
    return null
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        height: '3px',
        backgroundColor: 'rgba(0, 0, 0, 0.1)',
        pointerEvents: 'none',
      }}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress)}
      aria-label="Theme transition progress"
    >
      <div
        style={{
          height: '100%',
          width: `${progress}%`,
          backgroundColor: 'var(--color-primary)',
          transition: progress === 100 ? 'width 100ms ease-out' : 'none',
          boxShadow: '0 0 10px var(--color-primary)',
        }}
      />
    </div>
  )
}
